/* import __COLOCATED_TEMPLATE__ from './qa-monitor-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type MonitorsApi from 'embercom/services/monitors-api';
import type IntlService from 'ember-intl/services/intl';
import type { SelectedFilter } from 'embercom/services/filter-pattern-service';

interface Args {
  icon: string;
  name: string;
  initiallyOpened: boolean;
  removeFilter: () => void;
  setSelected: (filterValues: string[], operator: string) => void;
  selected: SelectedFilter;
  type: string;
  analyticsObject: any;
  isDisabled: boolean;
}

interface Signature {
  Args: Args;
}

interface FilterItem {
  text: string;
  value: string;
  isSelected: boolean;
}

export default class QaMonitorFilter extends Component<Signature> {
  @service declare monitorsApi: MonitorsApi;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked selectedDropdownItems = this.args.selected.values || [];
  @tracked operator = this.args.selected.operator || 'category';

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadMonitors).perform();
  }

  get isLoadingMonitors() {
    return taskFor(this.loadMonitors).isRunning;
  }

  get selectedMonitors() {
    return this.selectedDropdownItems.filter(
      (item: string) => item !== REPORTING_FILTER_SELECT_ALL,
    );
  }

  get allItems(): FilterItem[] {
    return this.monitorsApi.monitors
      .filter((monitor) => !monitor.archived)
      .map((monitor) => ({
        text: monitor.name,
        value: monitor.id,
        isSelected: this.selectedMonitors.includes(monitor.id),
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  get currentLabel() {
    if (
      this.selectedDropdownItems?.length &&
      this.selectedDropdownItems[0] !== REPORTING_FILTER_SELECT_ALL
    ) {
      return this.selectedDropdownItems
        .map((monitorId: string) => {
          let monitor = this.monitorsApi.monitors.find((m) => m.id === monitorId);
          return monitor?.name || monitorId;
        })
        .join(', ');
    }
    return this.intl.t('reporting.custom-reports.any');
  }

  @restartableTask
  *loadMonitors() {
    yield this.monitorsApi.fetchMonitors();
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_qa_monitor',
      object: this.args.analyticsObject,
      monitor: this.selectedMonitors,
      operator: this.operator,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::QaMonitorFilter': typeof QaMonitorFilter;
    'reporting/custom/chart-builder/filter-bar/qa-monitor-filter': typeof QaMonitorFilter;
  }
}
